import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { listProjects, type Project } from "@/services/project.service";
import {
	listMcpConnections,
	MCP_URL,
	type CreatedMcpConnection,
	type McpConnection,
} from "@/services/mcp-connection.service";
import { McpConnectionsList } from "./McpConnectionsList";
import { McpConnectionDialog } from "./McpConnectionDialog";
import { McpTokenDialog } from "./McpTokenDialog";
import { McpSetupSnippets } from "./McpSetupSnippets";
import { McpToolCatalog } from "./McpToolCatalog";
import { McpCodeBlock } from "./McpCodeBlock";

export function ProfileMcpSection() {
	const [connections, setConnections] = useState<McpConnection[]>([]);
	const [projects, setProjects] = useState<Project[]>([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editing, setEditing] = useState<McpConnection | null>(null);
	const [created, setCreated] = useState<CreatedMcpConnection | null>(null);

	useEffect(() => {
		Promise.all([listMcpConnections(), listProjects().catch(() => [])])
			.then(([c, p]) => {
				setConnections(c);
				setProjects(p);
			})
			.catch(() => toast.error("Failed to load MCP connections."))
			.finally(() => setLoading(false));
	}, []);

	const projectNames = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);

	function openDialog(c: McpConnection | null) {
		setEditing(c);
		setDialogOpen(true);
	}

	function handleCreated(c: CreatedMcpConnection) {
		setCreated(c);
		// Token lives only in the one-time dialog, never in the list state.
		setConnections((curr) => [{ ...c, token: undefined } as McpConnection, ...curr]);
	}

	function handleUpdated(c: McpConnection) {
		setConnections((curr) => curr.map((x) => (x.id === c.id ? c : x)));
	}

	function handleRevoked(id: string) {
		setConnections((curr) =>
			curr.map((x) => (x.id === id ? { ...x, revoked_at: new Date().toISOString() } : x)),
		);
	}

	return (
		<Card className="space-y-6 p-4 sm:p-6">
			<div>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div>
						<h3 className="text-sm font-semibold text-foreground">AI Agents (MCP)</h3>
						<p className="mt-0.5 text-xs text-muted-foreground">
							Connect Claude, Codex, Cursor or any MCP client to work with your projects, tasks
							and tickets.
						</p>
					</div>
					<Button type="button" size="sm" onClick={() => openDialog(null)} className="w-full sm:w-auto">
						<Plus className="h-3.5 w-3.5" />
						New connection
					</Button>
				</div>
				<Separator className="my-4" />
				{loading ? (
					<div className="space-y-2">
						<Skeleton className="h-8 w-full" />
						<Skeleton className="h-8 w-full" />
					</div>
				) : (
					<McpConnectionsList
						connections={connections}
						projectNames={projectNames}
						onEdit={openDialog}
						onRevoked={handleRevoked}
					/>
				)}
			</div>

			<div className="space-y-2">
				<h4 className="text-sm font-semibold text-foreground">Server URL</h4>
				<McpCodeBlock code={MCP_URL} />
				<p className="text-xs text-muted-foreground">
					Streamable HTTP transport, authenticated with <code>Authorization: Bearer thk_mcp_…</code>.
				</p>
				<McpSetupSnippets />
			</div>

			<McpToolCatalog />

			<McpConnectionDialog
				open={dialogOpen}
				editing={editing}
				projects={projects}
				onOpenChange={setDialogOpen}
				onCreated={handleCreated}
				onUpdated={handleUpdated}
			/>
			<McpTokenDialog created={created} onClose={() => setCreated(null)} />
		</Card>
	);
}
